import { useState, useMemo, useEffect } from 'react'
import {
	PostCollection,
	CardCollectionFilterControlsPanel,
	CardCollectionSearchControl,
	SortByControl
} from './index.js'
import {
	usePosts,
	// useTaxonomies
} from '../hooks/index.js'
import { flatten, pickAll, map, pipe, pluck, groupBy, uniqBy, prop, tap, pick, reject, propEq } from 'ramda'

import useSWRInfinite from 'swr/infinite'
import useSWR from "swr"

/** recursively fetches all pages of taxonomy terms for the provided taxonomy */
async function fetchTaxonomyTerms(taxonomy, page = 1) {
	// fetch the first page of terms
	const terms = await fetch(`${window.location.origin}/wp-json/wp/v2/${taxonomy.rest_base}?per_page=100&page=${page}`).then(res => {
		if (res.ok) {
			return res.json()
		}
		return []
	})
	// if there are more pages, fetch them
	if (terms.length === 100) {
		const nextTerms = await fetchTaxonomyTerms(taxonomy, page + 1)
		return [...terms, ...nextTerms]
	}
	return terms
}


function useTaxonomies(taxonomies = []) {
	const [taxonomiesData, setTaxonomiesData] = useState([])
	// for each taxonomy slug, fetch the taxonomy object
	useEffect(() => {
		Promise.all(taxonomies.map(async slug => {
			const taxonomy = await fetch(`${window.location.origin}/wp-json/wp/v2/taxonomies/${slug}`).then(res => {
				if (!res.ok) {
					return;
				}
				return res.json()
			})

			if (!taxonomy?._links['wp:items'][0]?.href) {
				return;
			}

			const terms = await fetchTaxonomyTerms(taxonomy)
			return {
				...taxonomy,
				terms
			}
		}))
			.then(taxonomies => {
				// filter out any undefined values
				setTaxonomiesData(taxonomies.filter(taxonomy => taxonomy))
			})


	}, [taxonomies])

	return taxonomiesData
}


function useTaxonomyTerms(taxonomies) {
	const [terms, setTerms] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(false)

	useEffect(() => {
		const fetchTerms = async () => {
			try {
				const termPromises = taxonomies
					.filter(
						(taxonomy) => taxonomy?._links['wp:items'][0]?.href
					).map((taxonomy) => {
						return fetch(
							`${window.location.origin}/wp-json/wp/v2/${taxonomy?.rest_base}?per_page=100`
						).then((res) => res.json())
							.catch(err => {
								console.error(err, taxonomy)
								return []
							})
					})
				const newTerms = await Promise.all(termPromises)
					.catch(err => {
						console.log(err)
						return []
					})
				setTerms(flatten(newTerms))
				setLoading(false)
			} catch (e) {
				console.error('Taxonomy terms error', e)
				setError(true)
				setLoading(false)
			}
		}
		fetchTerms()
	}, [taxonomies])

	return {
		terms,
		loading,
		error
	}
}

/** converts `sortBy` string to `{order, orderBy}` object */
function parseSortBy(sortBy) {
	const [orderBy, order] = sortBy.split('-')
	const validOrderBy = {
		date: 'date',
		alphabetical: 'title',
		featured: 'isFeatured'
	}
	return {
		order: order === 'desc' || orderBy === 'date' ? 'desc' : 'asc',
		orderBy: validOrderBy[orderBy] || 'date'
	}
}

export const FilteredPostCollection = (props = {}) => {
	const {
		query: {
			types,
			order: initialOrder = 'desc',
			orderBy: initialOrderBy = 'date',
			categories = [],
		},
		initialFilters = [],
		columns = 3,
		rows = 2,
		sortByEnabled = false,
		filterControlsEnabled = false,
		searchControlEnabled = false,
		searchControlPlaceholder = 'Enter keywords',
		searchControlLabel = 'Search',
		taxonomyFilters = [],
		loadMoreEnabled = false,
	} = props

	console.log(categories)

	const [sortBy, setSortBy] = useState(`${initialOrderBy}-${initialOrder}`)
	const [activeTerms, setActiveTerms] = useState({})
	const [searchTerms, setSearchTerms] = useState('')
	const [page, setPage] = useState(1)


	const { order = initialOrder, orderBy = initialOrderBy } = parseSortBy(sortBy)

	console.log('types', types)

	const {
		postsData,
		taxonomyData,
		postsError,
		isPostsValidating
	} = useSWRCustomerStories({
		categories,
		taxonomySlugs: taxonomyFilters,
		postTypes: types.map(type => type.slug),
	})



	const taxonomies = useMemo(() =>
		Object.values(taxonomyData || {}).filter(({ slug }) => taxonomyFilters.includes(slug)),
		[taxonomyData, taxonomyFilters]
	)


	const filters = useMemo(() => {

		const taxonomyFilters = taxonomies?.map(taxonomy => ({
			id: taxonomy.slug,
			label: `by ${taxonomy.name}`,
			name: taxonomy.slug,
			options: taxonomy.terms.map(term => ({
				id: term.id,
				label: term.name,
				value: term.slug,
				isActive: activeTerms[taxonomy.slug]?.includes(term.slug) || false
			}))
		})).sort((a, b) => a.name > b.name ? 1 : -1)
		return taxonomyFilters
	}, [taxonomies, activeTerms])


	const filteredPosts = useMemo(() => {
		// early return if no posts
		if (!postsData?.length) {
			return []
		}
		const activeTermsEntries = Object.entries(activeTerms).filter(([, terms]) => terms.length > 0)
		// return if activeTermsEntries is empty
		const postsMatchingActiveTerms = activeTermsEntries?.length === 0 ? postsData : postsData.filter(post => {
			// flatten the post's terms
			// const postTerms = flatten(post._embedded['wp:term'] || [])
			// post terms match at least one item in each of the active terms
			const postMatchesActiveTerms = activeTermsEntries.every(([taxonomy, terms]) => {
				return terms.some(term => post.terms.some(postTerm => postTerm.taxonomy === taxonomy && postTerm.slug === term))
			})

			return postMatchesActiveTerms
		}) || []

		const postsMatchingSearchTerms = searchTerms.length === 0 ? postsMatchingActiveTerms : postsMatchingActiveTerms.filter(post => {
			const postMatchesSearchTerms = post?.title.toLowerCase().includes(searchTerms.toLowerCase()) || post?.excerpt.toLowerCase().includes(searchTerms.toLowerCase())
			return postMatchesSearchTerms || post?.content.toLowerCase().includes(searchTerms.toLowerCase())
		}) || []
		return postsMatchingSearchTerms || []
	}, [postsData, activeTerms, searchTerms])

	const sortByTitle = (sortOrder, items) => {
		return items.sort((a, b) => {
			const aTitle = a.title.toLowerCase()
			const bTitle = b.title.toLowerCase()
			if (aTitle < bTitle) {
				return sortOrder === 'asc' ? -1 : 1
			}
			if (aTitle > bTitle) {
				return sortOrder === 'asc' ? 1 : -1
			}
			return 0
		})
	}

	const sortByDate = (sortOrder, items) => {
		return items.sort((a, b) => {
			const aDate = new Date(a.date)
			const bDate = new Date(b.date)
			if (aDate < bDate) {
				return sortOrder === 'asc' ? -1 : 1
			}
			if (aDate > bDate) {
				return sortOrder === 'asc' ? 1 : -1
			}
			return 0
		})
	}

	const sortByFeatured = (items) => {
		const featured = items.filter(item => item.isFeatured) || []
		const notFeatured = items.filter(item => !item.isFeatured) || []
		return [
			...sortByDate('desc', featured),
			...sortByDate('desc', notFeatured)
		]
	}

	const posts = useMemo(() => {
		if (filteredPosts.length === 0) {
			return []
		}
		if (sortBy === 'alphabetical') {
			return sortByTitle('asc', filteredPosts)
		}
		if (sortBy === 'alphabetical-reverse') {
			return sortByTitle('desc', filteredPosts)
		}

		if (sortBy === 'date-asc') {
			return sortByDate('asc', filteredPosts)
		}

		if (sortBy === 'date-desc') {
			return sortByDate('desc', filteredPosts)
		}

		if (sortBy === 'featured') {
			return sortByFeatured(filteredPosts)
		}

		return sortByTitle('desc', filteredPosts)
	}, [filteredPosts, sortBy])



	if (isPostsValidating && !filteredPosts?.length > 0) {
		return (
			<div class="grid p-40 items-center text-center mx-auto max-w-screen-xl" style={{
				zIndex: 99999
			}}>
				<div class="gap-4 grid p-6 text-2xl text-center">
					<svg version="1.1" id="L7" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 100 100" enable-background="new 0 0 100 100" class="block mx-auto w-20">
						<path fill="#3D74BA" d="M31.6,3.5C5.9,13.6-6.6,42.7,3.5,68.4c10.1,25.7,39.2,38.3,64.9,28.1l-3.1-7.9c-21.3,8.4-45.4-2-53.8-23.3 c-8.4-21.3,2-45.4,23.3-53.8L31.6,3.5z"><animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="2s" from="0 50 50" to="360 50 50" repeatCount="indefinite"></animateTransform></path>
						<path fill="#ADBD23" d="M42.3,39.6c5.7-4.3,13.9-3.1,18.1,2.7c4.3,5.7,3.1,13.9-2.7,18.1l4.1,5.5c8.8-6.5,10.6-19,4.1-27.7 c-6.5-8.8-19-10.6-27.7-4.1L42.3,39.6z"><animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="1s" from="0 50 50" to="-360 50 50" repeatCount="indefinite"></animateTransform></path>
						<path fill="#F9BB4E" d="M82,35.7C74.1,18,53.4,10.1,35.7,18S10.1,46.6,18,64.3l7.6-3.4c-6-13.5,0-29.3,13.5-35.3s29.3,0,35.3,13.5 L82,35.7z"><animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="2s" from="0 50 50" to="360 50 50" repeatCount="indefinite"></animateTransform></path>
					</svg>

					<span>Loading...</span>
				</div>
			</div>

		)
	}


	return (
		<div className='grid gap-6'>

			<div className="grid w-full gap-4 lg:grid-flow-cols" style={{
				gridTemplateColumns: sortByEnabled ? '1fr max-content max-content' : '1fr max-content'
			}}>
				{filterControlsEnabled && Object.values(taxonomies)?.length > 0 &&
					<CardCollectionFilterControlsPanel
						filters={filters}
						activeTerms={activeTerms}
						onChange={(changes) => {

							setActiveTerms(activeTerms => ({
								...activeTerms,
								...changes.filters
							}))
						}}
					/>
				}

				{sortByEnabled && <SortByControl onChange={setSortBy} value={sortBy} />}

				{searchControlEnabled && <CardCollectionSearchControl
					isEnabled={searchControlEnabled}
					placeholder={searchControlPlaceholder}
					label={searchControlLabel}
					onChange={(searchTerms) => {
						setSearchTerms(searchTerms)
					}}
				/>}


			</div>
			{posts?.length > 0 && (
				<PostCollection
					columns={columns}
					rows={rows}
					initialFilters={initialFilters}
					posts={posts}
					isLoading={posts.length < 1 && isPostsValidating}
					loadMoreEnabled={loadMoreEnabled}
					onLoadMoreClick={() => {
						setPage(page + 1)
					}}
					page={page}
				/>)}
		</div>
	)
}



function useSWRCustomerStories(props = {}) {
	const {
		taxonomySlugs = [],
		categories = [],
		postsPerPage = 12,
		page = 1,
		postTypes = ['post'],
		orderBy = 'date',
		order = 'desc',
	} = props

	const postTypesParams = `postTypes=${postTypes.join(',')}`
	// get posts
	const { data: postsData, error: postsError, isValidating: isPostsValidating } = useSWR(
		`/api/posts-by-type?per_page=100&${postTypesParams}`,
		(url) => fetch(url).then(res => res.json()),
		{
			onSuccess: (data) => {
				console.log('success', data)
			}
		}
	)

	const log = (label) => tap((x) => console.log(label, x))

	const slugNameMap = {
		'size': 'Business Size',
		'industry': 'Industry',
		'topic': 'Topic',
		'region': 'Region',
		'type': 'Type',
	}

	const getTaxonomyGroupsFromPosts = pipe(
		// gets the terms prop from each post
		pluck('terms'),
		// flattens the array of arrays into a single array
		flatten,
		// removes duplicates
		uniqBy(prop('id')),
		// groups the terms by taxonomy
		groupBy(prop('taxonomy')),
		// returns only the taxonomy groups we want
		pick(taxonomySlugs),
		// returns an array of objects like `{ slug: 'size', name: 'Business Size', terms: [...] }`
		(taxonomyGroups) => taxonomySlugs.map((slug) => ({
			slug,
			name: slugNameMap[slug],
			terms: taxonomyGroups[slug] || [],
		})),
		// omit any taxonomy groups that don't have any terms
		reject(propEq('terms', [])),
	)

	const taxonomyData = getTaxonomyGroupsFromPosts(postsData || [])



	return {
		postsData: filterByCategories(categories, (postsData || [])),
		taxonomyData,
		postsError,
		isPostsValidating,
	}
}

function filterByCategories(categoryIds, posts) {
	if (!categoryIds || categoryIds.length === 0) {
		return posts
	}

	return posts.filter((post) => {
		const termIds = getTermIdsFromTerms(post.terms)
		return categoryIds.some((categoryId) => termIds.includes(categoryId))
	})
}

function getTermIdsFromTerms(terms) {
	return terms.map((term) => term.id)
}

/** parses taxonomy data from customer stories posts Data */
function useTaxonomyData(taxonomies, postsData) {
	const taxonomyData = useMemo(() => {
		// early return if we have no posts
		if (!postsData) return null

		// flatten all terms into one array
		// all terms should be unique
		const terms = postsData.reduce((acc, post) => {
			for (const term of post.terms) {
				if (!acc.find(t => t.id === term.id)) {
					acc.push(term)
				}
			}
			return acc
		}, [])

		// group unique terms by taxonomy
		const termsByTaxonomy = taxonomies.map((slug) => {
			return {
				slug,
				terms: terms.filter(term => term.taxonomy === slug)
			}
		}).filter(taxonomy => taxonomy.terms.length > 0)
		return termsByTaxonomy
	}, [postsData])

	return taxonomyData
}

function localStorageProvider() {
	// When initializing, we restore the data from `localStorage` into a map.
	const map = new Map(JSON.parse(localStorage.getItem('app-cache') || '[]'))

	// Before unloading the app, we write back all the data into `localStorage`.
	window.addEventListener('beforeunload', () => {
		const appCache = JSON.stringify(Array.from(map.entries()))
		localStorage.setItem('app-cache', appCache)
	})

	// We still use the map for write & read for performance.
	return map
}
